import { useEffect, useState } from "react";
import { Copy, Check, Crown, Gift, Plus, Trash2, BookOpen, Smartphone, BarChart3, Music, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatCoins } from "../../lib/format";
import { FESTIVAL_THEMES } from "../../lib/festivals";

const inp = "rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm text-amber-100 focus:outline-none focus:border-amber-400";

const Toggle = ({ on, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full px-3 py-1 text-xs font-bold border ${on ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-rose-500/20 text-rose-300 border-rose-500/40"}`}>
    {on ? "ON" : "OFF"}
  </button>
);

export const CopyId = ({ id }) => {
  const [done, setDone] = useState(false);
  const copy = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(id); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = id; document.body.appendChild(ta); ta.select();
      const ok = document.execCommand("copy"); ta.remove();
      if (!ok) { toast.error("Copy failed"); return; }
    }
    setDone(true); toast.success("ID copied"); setTimeout(() => setDone(false), 1500);
  };
  return (
    <span role="button" tabIndex={0} onClick={copy} title="Copy ID" data-testid={`copy-id-${id}`}
      className="inline-grid place-items-center h-5 w-5 rounded text-cyan-300 hover:bg-cyan-500/15 transition-colors shrink-0 cursor-pointer">
      {done ? <Check size={12} /> : <Copy size={12} />}
    </span>
  );
};

export function FirstDepositCard({ config, setConfig }) {
  const fd = config.first_deposit;
  const set = (patch) => setConfig({ ...config, first_deposit: { ...fd, ...patch } });
  return (
    <div className="mw-card p-5" data-testid="first-deposit-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold flex items-center gap-2"><Gift size={18} className="text-fuchsia-400" /> First Deposit Bonus</h3>
        <Toggle on={fd.enabled} onClick={() => set({ enabled: !fd.enabled })} testid="first-deposit-toggle" />
      </div>
      <div className="flex flex-wrap gap-4">
        <label className="flex flex-col gap-1 text-xs text-slate-400">Bonus (%)
          <input type="number" min={0} max={500} value={fd.percent} onChange={(e) => set({ percent: Number(e.target.value) })} className={`${inp} w-28`} data-testid="first-deposit-percent" /></label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">Max bonus (₹)
          <input type="number" min={0} value={fd.max} onChange={(e) => set({ max: Number(e.target.value) })} className={`${inp} w-32`} data-testid="first-deposit-max" /></label>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">Player ki pehli deposit approve karte hi bonus apne aap balance me jud jayega.</p>
    </div>
  );
}

export function VipLevelsCard({ config, setConfig }) {
  const vip = config.vip;
  const set = (patch) => setConfig({ ...config, vip: { ...vip, ...patch } });
  const setLv = (i, patch) => set({ levels: vip.levels.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const add = () => {
    const last = vip.levels[vip.levels.length - 1];
    set({ levels: [...vip.levels, { name: "New", min_wagered: Number(last?.min_wagered || 0) * 2 || 1000, reward: 0 }] });
  };
  return (
    <div className="mw-card p-5" data-testid="vip-levels-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold flex items-center gap-2"><Crown size={18} className="text-amber-400" /> VIP Levels</h3>
        <Toggle on={vip.enabled} onClick={() => set({ enabled: !vip.enabled })} testid="vip-toggle" />
      </div>
      <div className="space-y-2">
        <div className="grid grid-cols-[1fr_1fr_1fr_32px] gap-2 text-[11px] uppercase tracking-wider text-slate-500">
          <span>Name</span><span>Kul dav (₹) se</span><span>Inaam (₹)</span><span />
        </div>
        {vip.levels.map((lv, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1fr_32px] gap-2" data-testid={`vip-level-${i}`}>
            <input value={lv.name} onChange={(e) => setLv(i, { name: e.target.value })} className={`${inp} min-w-0`} data-testid={`vip-name-${i}`} />
            <input type="number" min={0} value={lv.min_wagered} onChange={(e) => setLv(i, { min_wagered: Number(e.target.value) })} className={`${inp} min-w-0`} data-testid={`vip-min-${i}`} />
            <input type="number" min={0} value={lv.reward} onChange={(e) => setLv(i, { reward: Number(e.target.value) })} className={`${inp} min-w-0`} data-testid={`vip-reward-${i}`} />
            <button onClick={() => set({ levels: vip.levels.filter((_, j) => j !== i) })} disabled={vip.levels.length <= 1}
              data-testid={`vip-remove-${i}`} className="grid place-items-center rounded-lg text-rose-300 hover:bg-rose-500/15 disabled:opacity-30"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <button onClick={add} data-testid="vip-add-level" className="mt-3 flex items-center gap-1.5 rounded-lg border border-amber-500/30 px-3 py-1.5 text-xs font-bold text-amber-200 hover:border-amber-400"><Plus size={13} /> Level jodein</button>
      <p className="mt-2 text-[11px] text-slate-500">Level kul dav (total wagered) se badhta hai. Har level ka inaam player ek baar claim kar sakta hai.</p>
    </div>
  );
}

export function AppRulesCard({ config, setConfig }) {
  const app = config.app;
  const set = (patch) => setConfig({ ...config, app: { ...app, ...patch } });
  return (
    <div className="mw-card p-5 space-y-4" data-testid="app-rules-card">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold flex items-center gap-2"><Smartphone size={18} className="text-cyan-400" /> "App install karein" banner</h3>
        <Toggle on={app.install_prompt} onClick={() => set({ install_prompt: !app.install_prompt })} testid="install-prompt-toggle" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display font-bold flex items-center gap-2"><Music size={18} className="text-orange-400" /> Badi jeet par Damru + "Har Har Mahadev" awaaz</h3>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-400">Kam se kam jeet (₹)
            <input type="number" min={0} value={app.chime_min_win ?? 1000} onChange={(e) => set({ chime_min_win: Number(e.target.value) })} className={`${inp} w-28`} data-testid="chime-min-win" /></label>
          <Toggle on={app.chime_enabled ?? true} onClick={() => set({ chime_enabled: !(app.chime_enabled ?? true) })} testid="chime-toggle" />
        </div>
      </div>
      <div>
        <h3 className="font-display font-bold flex items-center gap-2 mb-2"><BookOpen size={18} className="text-amber-400" /> Game ke niyam aur sharten</h3>
        <textarea value={app.rules_text} onChange={(e) => set({ rules_text: e.target.value })} rows={12}
          className={`${inp} w-full font-sans leading-relaxed`} data-testid="rules-text-input" />
        <p className="mt-1 text-[11px] text-slate-500">Ye text players ko "नियम और शर्तें" पेज पर दिखेगा।</p>
      </div>
    </div>
  );
}

export function ColorRoundStats() {
  const [s, setS] = useState(null);
  useEffect(() => {
    const load = () => api.get("/admin/color/round-stats").then(({ data }) => setS(data)).catch(() => {});
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, []);
  if (!s) return null;
  return (
    <div className="mt-4 rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-3" data-testid="color-round-stats">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-xs font-bold uppercase tracking-wider text-cyan-300 flex items-center gap-1.5"><BarChart3 size={13} /> Is round ka kul dav</p>
        <span className="text-[11px] text-slate-400">
          <b className="font-mono-num text-amber-300" data-testid="round-stats-total">{formatCoins(s.total)}</b> · {s.players} players · {s.bets} bets
        </span>
      </div>
      {s.picks.length === 0 ? <p className="text-xs text-slate-500">Abhi is round me koi dav nahi laga</p> : (
        <>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {s.picks.map((p) => (
              <span key={p.pick} className="rounded-lg border border-white/10 bg-black/30 px-2 py-1 text-xs" data-testid={`round-pick-${p.pick}`}>
                <b className="text-slate-200">{p.pick}</b> <span className="font-mono-num text-amber-200">{formatCoins(p.total)}</span> <span className="text-slate-500">({p.count})</span>
              </span>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mb-1">Agar result ye number aaya to house profit / loss:</p>
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5">
            {s.outcomes.map((o) => (
              <div key={o.number} className={`rounded-lg border p-1.5 text-center ${o.house >= 0 ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/5"}`} data-testid={`round-outcome-${o.number}`}>
                <p className="font-mono-num font-bold text-sm">{o.number}</p>
                <p className={`font-mono-num text-[10px] ${o.house >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{o.house >= 0 ? "+" : ""}{o.house}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function FestivalCard({ config, setConfig }) {
  const fe = config.festival;
  const set = (patch) => setConfig({ ...config, festival: { ...fe, ...patch } });
  const field = (label, key, type = "text", cls = "w-full") => (
    <label className="flex flex-col gap-1 text-xs text-slate-400">{label}
      <input type={type} value={fe[key] ?? ""} onChange={(e) => set({ [key]: type === "number" ? Number(e.target.value) : e.target.value })}
        className={`${inp} ${cls}`} data-testid={`festival-${key.replace(/_/g, "-")}`} /></label>
  );
  return (
    <div className="mw-card p-5 border-orange-400/40" data-testid="festival-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold flex items-center gap-2"><Sparkles size={18} className="text-orange-400" /> Tyohar Special</h3>
        <Toggle on={fe.enabled} onClick={() => set({ enabled: !fe.enabled })} testid="festival-toggle" />
      </div>
      <p className="text-xs text-slate-400 mb-2">Tyohar chunein (title, subtitle aur banner apne aap bhar jayenge):</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(FESTIVAL_THEMES).map(([key, th]) => (
          <button key={key} data-testid={`festival-preset-${key}`}
            onClick={() => set({ theme: key, title: th.title, subtitle: th.subtitle, banner_url: th.banner_url })}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${(fe.theme || "shivratri") === key ? "border-amber-400 bg-amber-500/20 text-amber-100" : "border-white/15 text-slate-300 hover:border-amber-400/60"}`}>
            <span aria-hidden>{th.icon}</span> {th.name}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {field("Title", "title")}
        {field("Subtitle", "subtitle")}
        {field("Shuru (date)", "start_date", "date")}
        {field("Khatam (date)", "end_date", "date")}
        {field("Har deposit par bonus (%)", "bonus_percent", "number")}
        {field("Max bonus (₹)", "bonus_max", "number")}
        <div className="sm:col-span-2">{field("Banner image URL", "banner_url")}</div>
      </div>
      {fe.banner_url && <img src={fe.banner_url} alt="" className="mt-3 h-28 rounded-xl object-cover ring-1 ring-amber-400/30" />}
      <p className="mt-2 text-[11px] text-slate-500">ON hone par aur in dino ke beech (IST) Lobby + Payments me banner dikhega aur har approve hui deposit par bonus apne aap judega. Bonus 0% rakhne par sirf banner dikhega.</p>
    </div>
  );
}

export function DailySpinCard({ config, setConfig }) {
  const ds = config.daily_spin;
  const set = (patch) => setConfig({ ...config, daily_spin: { ...ds, ...patch } });
  const setP = (i, patch) => set({ prizes: ds.prizes.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const total = ds.prizes.reduce((a, p) => a + Number(p.weight || 0), 0) || 1;
  return (
    <div className="mw-card p-5" data-testid="daily-spin-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold flex items-center gap-2"><Sparkles size={18} className="text-amber-400" /> Bhole Daily Spin (roz 1 muft spin)</h3>
        <Toggle on={ds.enabled} onClick={() => set({ enabled: !ds.enabled })} testid="daily-spin-toggle" />
      </div>
      <div className="grid grid-cols-[1fr_1fr_1fr_60px_32px] gap-2 text-[11px] uppercase tracking-wider text-slate-500 mb-1">
        <span>Wheel par likha</span><span>Inaam (₹)</span><span>Mauka (weight)</span><span>%</span><span />
      </div>
      <div className="space-y-2">
        {ds.prizes.map((p, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1fr_60px_32px] gap-2 items-center" data-testid={`spin-prize-${i}`}>
            <input value={p.label} onChange={(e) => setP(i, { label: e.target.value })} className={`${inp} min-w-0`} data-testid={`spin-prize-label-${i}`} />
            <input type="number" min={0} value={p.amount} onChange={(e) => setP(i, { amount: Number(e.target.value) })} className={`${inp} min-w-0`} data-testid={`spin-prize-amount-${i}`} />
            <input type="number" min={0} step={0.5} value={p.weight} onChange={(e) => setP(i, { weight: Number(e.target.value) })} className={`${inp} min-w-0`} data-testid={`spin-prize-weight-${i}`} />
            <span className="font-mono-num text-xs text-slate-400">{((Number(p.weight || 0) / total) * 100).toFixed(1)}%</span>
            <button onClick={() => set({ prizes: ds.prizes.filter((_, j) => j !== i) })} disabled={ds.prizes.length <= 2}
              data-testid={`spin-prize-remove-${i}`} className="grid place-items-center rounded-lg text-rose-300 hover:bg-rose-500/15 disabled:opacity-30 h-9"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <button onClick={() => set({ prizes: [...ds.prizes, { label: "₹25", amount: 25, weight: 5 }] })} disabled={ds.prizes.length >= 12}
        data-testid="spin-prize-add" className="mt-3 flex items-center gap-1.5 rounded-lg border border-amber-500/30 px-3 py-1.5 text-xs font-bold text-amber-200 hover:border-amber-400 disabled:opacity-40"><Plus size={13} /> Inaam jodein</button>
      <p className="mt-2 text-[11px] text-slate-500">Har player roz (IST raat 12 baje reset) ek baar muft spin kar sakta hai. Average kharch per spin: <b className="text-amber-200">{formatCoins(Math.round(ds.prizes.reduce((a, p) => a + Number(p.amount || 0) * Number(p.weight || 0), 0) / total))}</b></p>
    </div>
  );
}
